import {
  DEFAULT_CURRENCY,
  boardContents,
  boardMeta,
  boardStatus,
  outfitBudgetLine,
  summarize,
  type User,
} from '@closetly/core';
import { useState } from 'react';
import { useItemEditor } from '../lib/editor';
import { useMembership } from '../lib/membership';
import { useDeleteBoard, useItems, useMe, useSaveBoard } from '../lib/queries';
import { navigate } from '../lib/router';
import { useToast } from '../lib/toast';
import { BoardNameSheet } from './BoardNameSheet';
import { ItemTile } from './ItemTile';
import { OutfitPin } from './OutfitPin';
import { ShareSheet } from './ShareSheet';

/** One board: its outfits and single pieces on one Pinterest-style wall. */
export function BoardDetail({ user, id }: { user: User; id: string }) {
  const m = useMembership(user.id);
  const items = useItems(user.id);
  const me = useMe(user);
  const saveBoard = useSaveBoard(user.id);
  const deleteBoard = useDeleteBoard(user.id);
  const editor = useItemEditor();
  const toast = useToast();
  const [renaming, setRenaming] = useState(false);
  const [sharing, setSharing] = useState(false);
  const currency = me.data?.currency || DEFAULT_CURRENCY;

  const board = m.boards.find((b) => b.id === id);
  if (!board) {
    return (
      <div className="empty">
        <strong>Board nicht gefunden</strong>
        <button type="button" className="btn" onClick={() => navigate('/boards')}>
          Zu den Boards
        </button>
      </div>
    );
  }

  const all = items.data ?? [];
  const c = boardContents(board, m.outfits, all);
  const status = boardStatus(c.pieces);
  const budget = summarize(all, me.data?.budget_cents ?? 0, currency).budget;
  const budgetLine = outfitBudgetLine(status, budget ? budget.leftCents : null, currency);

  function remove() {
    if (!board || !confirm(`Board „${board.name}“ löschen? Outfits und Teile bleiben erhalten.`)) return;
    deleteBoard.mutate({ id: board.id, owner: user.id }, { onError: () => toast('Löschen fehlgeschlagen.') });
    navigate('/boards', { replace: true });
    toast('Board gelöscht');
  }

  return (
    <>
      <button type="button" className="back linkbtn" onClick={() => navigate('/boards')}>
        ‹ Alle Boards
      </button>
      <div className="board-head">
        <div>
          <h2>{board.name}</h2>
          <p className="hint">
            {boardMeta(c, status, currency)}
            {budgetLine && ` · ${budgetLine}`}
          </p>
        </div>
        <div className="detail-actions">
          <button type="button" className="btn" onClick={() => editor.openNew({ boardId: board.id })}>
            + Teil per Link
          </button>
          <button type="button" className="btn" onClick={() => setSharing(true)}>
            Teilen
          </button>
          <button type="button" className="btn" onClick={() => setRenaming(true)}>
            Umbenennen
          </button>
          <button type="button" className="btn danger" onClick={remove}>
            Löschen
          </button>
        </div>
      </div>

      {c.outfits.length + c.items.length === 0 ? (
        <div className="empty">
          <strong>Noch leer</strong>
          Tippe bei einem Teil oder Outfit auf „Merken“ und wähle „{board.name}“.
        </div>
      ) : (
        <div className="board">
          {c.outfits.map((o) => (
            <OutfitPin key={o.id} outfit={o} items={all} currency={currency} />
          ))}
          {c.items.map((i) => (
            <ItemTile
              key={i.id}
              item={i}
              onOpen={editor.openItem}
              onSave={(it) => editor.openSave({ type: 'item', id: it.id, name: it.name })}
            />
          ))}
        </div>
      )}

      {sharing && (
        <ShareSheet uid={user.id} target={{ kind: 'board', id: board.id, name: board.name }} onClose={() => setSharing(false)} />
      )}
      {renaming && (
        <BoardNameSheet
          title="Board umbenennen"
          initial={board.name}
          onClose={() => setRenaming(false)}
          onSave={(name) => {
            setRenaming(false);
            saveBoard.mutate({ id: board.id, owner: user.id, name }, { onError: () => toast('Speichern fehlgeschlagen.') });
          }}
        />
      )}
    </>
  );
}
